/**
 * The list card's toolbar: the compact search, the filter square, and the preview-panel toggle.
 *
 * ── The dot counts the FACETS and not the scope ─────────────────────────────────────────────
 * The scope switch four inches above says which list this is, in words, with a count beside each.
 * A dot on the square that lit merely because the trainer is on `Twoje` would be the square
 * claiming a filter the trainer can already see — with `Wyczyść` the only way out of something that
 * is not in their way. `useExerciseLibrary` counts `muscles + equipment + types` for exactly this.
 *
 * ── The toggle is `aria-pressed`, not a pair of buttons ─────────────────────────────────────
 * One control, two states, and the state is announced rather than inferred from which of two icons
 * is drawn. Its accessible name says what it IS (`Panel podglądu`) and its title says what pressing
 * it will do (`Ukryj panel` / `Pokaż panel`), which is the split a toggle button needs — a name
 * that changed with the state would make a screen reader announce a different control each press.
 *
 * ── And it is NOT DRAWN where there is nothing to toggle ─────────────────────────────────────
 * Below `PANEL_FITS_QUERY` the column is not on the screen whatever the trainer prefers, so the
 * control is absent rather than reporting a state about something nobody can see. It used to read
 * `aria-pressed` off the STORED value, which at 1300px announced a panel that was not there and
 * wrote `hidden` for the wide window the trainer would next open. `panel` is `null` there, which is
 * one prop rather than a state and a flag that can disagree.
 */

import type { RefObject } from 'react';
import { Popover } from '@base-ui/react/popover';
import { useTranslation } from 'react-i18next';

import { SearchField } from '~/shared/ui/SearchField';
import { FilterIcon } from '~/shared/ui/FilterIcon';

import type { ExerciseCatalogueView, ExerciseQuery } from '~/entities/exercise-library';
import type { ExerciseScope } from '../model/exerciseScope';

import { ExerciseFilterPanel } from './ExerciseFilterPanel';
import { PanelToggleGlyph } from './ExerciseLibraryIcons';
import square from '~/shared/ui/filterSquare.module.css';
import styles from './ExerciseToolbar.module.css';

interface ExerciseToolbarProps {
  readonly query: ExerciseQuery;
  readonly onQueryChange: (query: ExerciseQuery) => void;
  readonly catalogue: ExerciseCatalogueView;
  readonly activeFacetCount: number;
  readonly scope: ExerciseScope;
  readonly onScopeChange: (scope: ExerciseScope) => void;
  readonly matchCount: number;
  readonly disabled: boolean;
  /** The preview column's toggle, or `null` where the window has no room for the column. */
  readonly panel: { readonly shown: boolean; readonly onToggle: () => void } | null;
  /** The list card, which the filter panel hangs off its top-left corner. */
  readonly anchor: RefObject<HTMLElement | null>;
  readonly searchRef: RefObject<HTMLInputElement | null>;
  /** Board D's „Do uzupełnienia" toggle: the marked movements' count (`null` until read) and whether only they show. */
  readonly review: { readonly count: number | null; readonly only: boolean; readonly toggle: () => void };
  /** The filter panel's „Zarządzaj tagami". */
  readonly onManageTags: () => void;
}

export function ExerciseToolbar({
  query,
  onQueryChange,
  catalogue,
  activeFacetCount,
  scope,
  onScopeChange,
  matchCount,
  disabled,
  panel,
  anchor,
  searchRef,
  review,
  onManageTags,
}: ExerciseToolbarProps) {
  const { t } = useTranslation();
  const placeholder = t('web:exerciseLibrary.searchPlaceholder');

  return (
    <div className={styles.toolbar}>
      <SearchField
        inputRef={searchRef}
        size="compact"
        className={styles.search}
        value={query.text}
        onChange={(text) => {
          onQueryChange({ ...query, text });
        }}
        // The field's accessible name is the placeholder's sentence: a placeholder disappears on the
        // first keystroke, so it cannot be the name, and the two say the same thing for a reason.
        label={placeholder}
        placeholder={placeholder}
        clearLabel={t('web:exerciseLibrary.clearSearch')}
        disabled={disabled}
      />

      <ExerciseFilterPanel
        query={query}
        onQueryChange={onQueryChange}
        catalogue={catalogue}
        scope={scope}
        onScopeChange={onScopeChange}
        matchCount={matchCount}
        anchor={anchor}
        onManageTags={onManageTags}
      >
        {/* An icon-only control, so it carries its whole name in `aria-label` and its glyph is
          * hidden. The dot is a second channel on the same fact and is decorative. */}
        <Popover.Trigger
          className={square.square}
          title={t('web:exerciseLibrary.filters.open')}
          aria-label={t(
            activeFacetCount === 0
              ? 'web:exerciseLibrary.filters.open'
              : 'web:exerciseLibrary.filters.active',
          )}
        >
          <FilterIcon className={square.icon} />
          {activeFacetCount > 0 && <span className={square.dot} aria-hidden />}
        </Popover.Trigger>
      </ExerciseFilterPanel>

      {/* Board D: the movements added from Buddy's proposals and not yet checked, in the list on
        * screen. Neutral, pressed as ink; the count is part of its name. */}
      <button
        type="button"
        className={`${String(styles.review)} ${review.only ? String(styles.reviewOn) : ''}`}
        aria-pressed={review.only}
        aria-disabled={disabled ? 'true' : undefined}
        onClick={disabled ? undefined : review.toggle}
      >
        {t('web:exerciseLibrary.review.filter')}{' '}
        {/* The figure's room stands from the first frame; until the catalogue is read it holds no
          * number at all, never a zero the read would contradict. */}
        <span className={styles.reviewCount}>{review.count === null ? null : <b className={styles.reviewFigure}>{review.count}</b>}</span>
      </button>

      {panel !== null && (
        <>
          <span className={styles.separator} aria-hidden />

          <button
            type="button"
            className={`${String(square.square)} ${panel.shown ? String(styles.toggleOn) : ''}`}
            aria-pressed={panel.shown}
            aria-label={t('web:exerciseLibrary.panel.name')}
            title={t(
              panel.shown ? 'web:exerciseLibrary.panel.hide' : 'web:exerciseLibrary.panel.show',
            )}
            onClick={panel.onToggle}
          >
            <PanelToggleGlyph className={styles.toggleIcon} />
          </button>
        </>
      )}
    </div>
  );
}
